import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { fmtCurrency, fmtDate, statusLabel } from '../lib/utils';
import { Card, Stat, SH, Avatar, Badge, Grid4, Spinner, PageHeader, B } from '../components/UI';

export default function DashboardPage() {
  const [jobs, setJobs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    async function load() {
      const [{ data: j }, { data: inv }] = await Promise.all([
        supabase.from('jobs').select('id,status,contract_total,amount_paid,customer_name,type,created_at').order('created_at', { ascending: false }),
        supabase.from('invoices').select('id,amount,status,description,job_id,customer_name,due_date').eq('status', 'pending'),
      ]);
      setJobs(j || []);
      setInvoices(inv || []);
      setLoading(false);
    }
    load();
  }, []);

  if (loading) return <Spinner />;

  const active = jobs.filter(j => j.status === 'in-progress').length;
  const scheduled = jobs.filter(j => ['scheduled', 'final-inspection'].includes(j.status)).length;
  const completed = jobs.filter(j => j.status === 'completed').length;
  const totalRev = jobs.reduce((s, j) => s + (j.contract_total || 0), 0);
  const totalColl = jobs.reduce((s, j) => s + (j.amount_paid || 0), 0);
  const outstanding = totalRev - totalColl;
  const recent = jobs.slice(0, 5);

  return (
    <div style={{ padding: '1.5rem' }}>
      <PageHeader title="Dashboard" sub="Nighthawk Roofing — business overview" />

      <Grid4 style={{ marginBottom: '1.25rem' }}>
        <Stat label="Active Jobs" value={active} sub="On-site now" accent={B.navy} />
        <Stat label="Scheduled" value={scheduled} sub="Upcoming & inspection" accent={B.info} />
        <Stat label="Completed" value={completed} accent="#16A34A" />
        <Stat label="Total Pipeline" value={jobs.length} sub="All projects" />
      </Grid4>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: '1.5rem' }}>
        <Stat label="Contract Value" value={fmtCurrency(totalRev)} sub="Total pipeline" accent={B.navy} />
        <Stat label="Collected" value={fmtCurrency(totalColl)} sub={totalRev > 0 ? `${Math.round(totalColl / totalRev * 100)}% of total` : ''} accent="#16A34A" />
        <Stat label="Outstanding" value={fmtCurrency(outstanding)} accent={outstanding > 0 ? '#D97706' : '#16A34A'} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
        <Card>
          <SH title="Recent Jobs" action={<button onClick={() => navigate('/jobs')} style={{ fontSize: 12, padding: '5px 10px', borderRadius: 8, border: '1px solid #D1D5DB', background: '#fff', cursor: 'pointer', fontFamily: "'Barlow',sans-serif", fontWeight: 600 }}>View all</button>} />
          {recent.length === 0 && <p style={{ fontSize: 13, color: '#94A3B8' }}>No jobs yet.</p>}
          {recent.map(j => (
            <div key={j.id} onClick={() => navigate(`/jobs/${j.id}`)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, cursor: 'pointer', marginBottom: 6, border: '1px solid #F1F5F9' }}
              onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <Avatar name={j.customer_name} size={30} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: B.navy }}>{j.id.slice(0, 8)}… — {j.customer_name}</div>
                <div style={{ fontSize: 11, color: '#94A3B8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{j.type}</div>
              </div>
              <Badge status={j.status} />
            </div>
          ))}
        </Card>

        <Card>
          <SH title="Pending Invoices" action={<button onClick={() => navigate('/invoices')} style={{ fontSize: 12, padding: '5px 10px', borderRadius: 8, border: '1px solid #D1D5DB', background: '#fff', cursor: 'pointer', fontFamily: "'Barlow',sans-serif", fontWeight: 600 }}>View all</button>} />
          {invoices.length === 0 && <p style={{ fontSize: 13, color: '#94A3B8' }}>All clear — no pending invoices!</p>}
          {invoices.slice(0, 6).map(inv => (
            <div key={inv.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: '#FFFBEB', borderRadius: 8, border: '1px solid #FDE68A', marginBottom: 6 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: B.navy }}>{inv.description || inv.id}</div>
                <div style={{ fontSize: 11, color: '#94A3B8' }}>{inv.customer_name}{inv.due_date ? ` · Due: ${fmtDate(inv.due_date)}` : ''}</div>
              </div>
              <div style={{ fontSize: 15, fontWeight: 800, fontFamily: "'Barlow Condensed',sans-serif", color: '#D97706' }}>{fmtCurrency(inv.amount)}</div>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}
